import { useState } from 'react';
import { useAdminPlans, useUpdatePlan, useApplyPlanLatest } from '../lib/queries.js';
import type { AdminPlan } from '../lib/queries.js';

export function PlansPage() {
  const { data, isLoading } = useAdminPlans();
  const updatePlan = useUpdatePlan();
  const applyLatest = useApplyPlanLatest();
  const [selected, setSelected] = useState<AdminPlan | null>(null);

  // Form States
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priceBrl, setPriceBrl] = useState(0);
  const [priceUsd, setPriceUsd] = useState<number | null>(null);
  const [trialDays, setTrialDays] = useState<number | null>(null);
  const [trialRequiresCard, setTrialRequiresCard] = useState(false);
  const [isActiveState, setIsActiveState] = useState(true);
  const [isPublicState, setIsPublicState] = useState(true);
  const [featuresState, setFeaturesState] = useState<any>({});
  const [aiLimitsState, setAiLimitsState] = useState<any>({});
  
  // Tabs State
  const [activeTab, setActiveTab] = useState<'geral' | 'features' | 'ai'>('geral');
  const [selectedModule, setSelectedModule] = useState<'suggest' | 'summary' | 'autolink' | 'rag_chat' | 'voice_agent' | 'embedding'>('suggest');

  function openEdit(p: AdminPlan) {
    setSelected(p);
    setName(p.name);
    setDescription(p.description || '');
    setPriceBrl(p.priceBrl);
    setPriceUsd(p.priceUsd);
    setTrialDays(p.trialDays);
    setTrialRequiresCard(p.trialRequiresCard);
    setIsActiveState(p.isActive);
    setIsPublicState(p.isPublic);
    setFeaturesState({ ...(p.features || {}) });
    setAiLimitsState({ ...(p.aiLimits || {}) });
    setActiveTab('geral');
  }

  const handleFeatureToggle = (key: string) => {
    setFeaturesState((prev: any) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleFeatureNumber = (key: string, val: number | null) => {
    setFeaturesState((prev: any) => ({
      ...prev,
      [key]: val,
    }));
  };

  const handleAILimitToggle = (key: string) => {
    setAiLimitsState((prev: any) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleModuleLimitChange = (modKey: string, field: string, value: any) => {
    setAiLimitsState((prev: any) => {
      const modules = { ...(prev.modules || {}) };
      const mod = { ...(modules[modKey] || {}) };
      mod[field] = value;
      modules[modKey] = mod;
      return { ...prev, modules };
    });
  };

  async function handleSave() {
    if (!selected) return;
    try {
      await updatePlan.mutateAsync({
        slug: selected.slug,
        name,
        description: description || null,
        price_brl: priceBrl,
        price_usd: priceUsd,
        trial_days: trialDays,
        trial_requires_card: trialRequiresCard,
        features: featuresState,
        ai_limits: aiLimitsState,
        is_active: isActiveState,
        is_public: isPublicState,
      });
      setSelected(null);
      alert('Plano atualizado com sucesso!');
    } catch (err: any) {
      alert(`Erro ao salvar: ${err.message || err}`);
    }
  }

  if (isLoading) return <div className="text-zinc-500 font-semibold p-4">carregando…</div>;

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Planos de Assinatura</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data?.plans.map((p) => (
          <button
            key={p.id}
            onClick={() => openEdit(p)}
            className="text-left rounded-lg border border-zinc-800 bg-zinc-900/30 p-5 hover:border-cyan-500 transition-all hover:bg-zinc-900/50 group"
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-bold text-zinc-100 group-hover:text-cyan-400 transition-colors">{p.name}</div>
                <div className="text-[10px] text-zinc-500 font-mono">{p.slug}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-zinc-100">
                  R$ {(p.priceBrl / 100).toFixed(2)}
                </div>
                {p.trialDays && (
                  <div className="text-xs text-zinc-500">trial {p.trialDays}d</div>
                )}
              </div>
            </div>
            <div className="text-xs text-zinc-400 leading-relaxed mb-4">{p.description}</div>
            <div className="flex gap-2 text-[10px] font-mono font-bold">
              <span className={`rounded-md px-2 py-0.5 border ${p.isActive ? 'bg-emerald-950/60 text-emerald-400 border-emerald-900/30' : 'bg-zinc-800/80 text-zinc-500 border-zinc-750'}`}>
                {p.isActive ? 'ATIVO' : 'INATIVO'}
              </span>
              <span className={`rounded-md px-2 py-0.5 border ${p.isPublic ? 'bg-cyan-950/60 text-cyan-400 border-cyan-900/30' : 'bg-zinc-800/80 text-zinc-500 border-zinc-750'}`}>
                {p.isPublic ? 'PÚBLICO' : 'PRIVADO'}
              </span>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/65 grid place-items-center p-6 z-50 overflow-y-auto" onClick={() => setSelected(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-zinc-900 border border-zinc-800 rounded-lg max-w-3xl w-full max-h-[92dvh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-zinc-800/60 bg-zinc-950/20 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-zinc-100">Editar Plano: {selected.name}</h2>
                <p className="text-[10px] text-zinc-500 font-mono mt-0.5">{selected.slug}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-zinc-400 hover:text-zinc-100 text-xs bg-zinc-800 hover:bg-zinc-750 px-3 py-1.5 rounded transition-all font-semibold"
              >
                Fechar
              </button>
            </div>

            {/* Navigation Tabs */}
            <div className="px-6 border-b border-zinc-850 bg-zinc-950/10 flex gap-2 pt-2">
              <button
                onClick={() => setActiveTab('geral')}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
                  activeTab === 'geral' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
              >
                Configurações Gerais
              </button>
              <button
                onClick={() => setActiveTab('features')}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
                  activeTab === 'features' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
              >
                Recursos (Features)
              </button>
              <button
                onClick={() => setActiveTab('ai')}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
                  activeTab === 'ai' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
              >
                Inteligência Artificial (IA)
              </button>
            </div>

            {/* Tab Contents */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              
              {/* Tab 1: Geral */}
              {activeTab === 'geral' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500 font-bold block mb-1">Nome do Plano</label>
                      <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-zinc-500 font-bold block mb-1">Preço em Real (BRL)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-zinc-500 text-sm">R$</span>
                        <input
                          type="number"
                          value={(priceBrl / 100).toString()}
                          onChange={(e) => setPriceBrl(Math.round(parseFloat(e.target.value || '0') * 100))}
                          className="w-full bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-cyan-500 font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase text-zinc-500 font-bold block mb-1">Descrição</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-cyan-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-zinc-950/40 border border-zinc-800/60 p-4 rounded-lg flex items-center justify-between">
                      <div>
                        <span className="text-sm font-semibold text-zinc-200 block">Status de Ativação</span>
                        <span className="text-xs text-zinc-500">Determina se novas assinaturas podem selecionar este plano.</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={isActiveState}
                        onChange={(e) => setIsActiveState(e.target.checked)}
                        className="w-4 h-4 rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0"
                      />
                    </div>

                    <div className="bg-zinc-950/40 border border-zinc-800/60 p-4 rounded-lg flex items-center justify-between">
                      <div>
                        <span className="text-sm font-semibold text-zinc-200 block">Plano Público</span>
                        <span className="text-xs text-zinc-500">Exibido na página de Checkout padrão para novos usuários.</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={isPublicState}
                        onChange={(e) => setIsPublicState(e.target.checked)}
                        className="w-4 h-4 rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0"
                      />
                    </div>
                  </div>

                  {/* Trial configuration */}
                  <div className="bg-zinc-950/20 border border-zinc-800 rounded-lg p-4 space-y-4">
                    <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Período de Testes (Trial)</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] uppercase text-zinc-500 font-bold block mb-1">Dias de Trial</label>
                        <input
                          type="number"
                          value={trialDays || 0}
                          onChange={(e) => setTrialDays(parseInt(e.target.value || '0') || null)}
                          className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-cyan-500 font-mono"
                        />
                      </div>

                      <div className="flex items-center justify-between pt-5">
                        <div>
                          <span className="text-xs font-semibold text-zinc-200 block">Requer Cartão</span>
                          <span className="text-[10px] text-zinc-500">Usuário deve preencher dados de pagamento para trial.</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={trialRequiresCard}
                          onChange={(e) => setTrialRequiresCard(e.target.checked)}
                          className="w-4 h-4 rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 2: Features */}
              {activeTab === 'features' && (
                <div className="space-y-6">
                  
                  {/* Limits */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-zinc-950/30 border border-zinc-800/60 p-4 rounded-lg">
                      <label className="text-[10px] uppercase text-zinc-500 font-bold block mb-2">Max Workspaces</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          disabled={featuresState.workspaces_max === null}
                          value={featuresState.workspaces_max || 0}
                          onChange={(e) => handleFeatureNumber('workspaces_max', parseInt(e.target.value) || 0)}
                          className="w-24 bg-zinc-950 border border-zinc-850 rounded px-2.5 py-1 text-xs focus:outline-none focus:border-cyan-500 disabled:opacity-40 text-zinc-200 font-mono"
                        />
                        <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={featuresState.workspaces_max === null}
                            onChange={(e) => handleFeatureNumber('workspaces_max', e.target.checked ? null : 5)}
                            className="rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0"
                          />
                          Sem limite
                        </label>
                      </div>
                    </div>

                    <div className="bg-zinc-950/30 border border-zinc-800/60 p-4 rounded-lg">
                      <label className="text-[10px] uppercase text-zinc-500 font-bold block mb-2">Max Documentos</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          disabled={featuresState.docs_max === null}
                          value={featuresState.docs_max || 0}
                          onChange={(e) => handleFeatureNumber('docs_max', parseInt(e.target.value) || 0)}
                          className="w-24 bg-zinc-950 border border-zinc-850 rounded px-2.5 py-1 text-xs focus:outline-none focus:border-cyan-500 disabled:opacity-40 text-zinc-200 font-mono"
                        />
                        <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={featuresState.docs_max === null}
                            onChange={(e) => handleFeatureNumber('docs_max', e.target.checked ? null : 100)}
                            className="rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0"
                          />
                          Sem limite
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-zinc-950/30 border border-zinc-800/60 p-4 rounded-lg">
                      <label className="text-[10px] uppercase text-zinc-500 font-bold block mb-1">Armazenamento de Assets (MB)</label>
                      <input
                        type="number"
                        value={featuresState.assets_mb || 0}
                        onChange={(e) => handleFeatureNumber('assets_mb', parseInt(e.target.value) || 0)}
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:border-cyan-500 font-mono"
                      />
                    </div>

                    <div className="bg-zinc-950/30 border border-zinc-800/60 p-4 rounded-lg">
                      <label className="text-[10px] uppercase text-zinc-500 font-bold block mb-1">Assentos (Seats / Membros)</label>
                      <input
                        type="number"
                        value={featuresState.seats || 1}
                        onChange={(e) => handleFeatureNumber('seats', parseInt(e.target.value) || 1)}
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:border-cyan-500 font-mono"
                      />
                    </div>
                  </div>

                  {/* Feature Switches */}
                  <div className="border-t border-zinc-800/60 pt-4">
                    <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3">Capacidades e Acessos</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { key: 'collab', label: 'Colaboração em Tempo Real', desc: 'Permite múltiplos membros editando o grafo simultaneamente.' },
                        { key: 'share_public', label: 'Compartilhamento Público', desc: 'Permite publicar grafos em links públicos somente-leitura.' },
                        { key: 'export_pdf', label: 'Exportação para PDF', desc: 'Permite exportar documentos e resumos em arquivos PDF.' },
                        { key: 'api_access', label: 'Acesso à API Externa', desc: 'Habilita chaves de API para integrações de terceiros.' }
                      ].map((feat) => (
                        <div key={feat.key} className="bg-zinc-950/40 border border-zinc-800/50 p-3 rounded-lg flex items-center justify-between">
                          <div>
                            <span className="text-xs font-bold text-zinc-200 block">{feat.label}</span>
                            <span className="text-[10px] text-zinc-500">{feat.desc}</span>
                          </div>
                          <input
                            type="checkbox"
                            checked={!!featuresState[feat.key]}
                            onChange={() => handleFeatureToggle(feat.key)}
                            className="w-4 h-4 rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 3: AI Limits */}
              {activeTab === 'ai' && (
                <div className="space-y-6">
                  
                  {/* General AI Config */}
                  <div className="bg-zinc-950/40 border border-zinc-800 p-4 rounded-lg space-y-4">
                    <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Configurações Gerais de IA</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center justify-between bg-zinc-900/60 border border-zinc-850 p-3 rounded-lg">
                        <div>
                          <span className="text-xs font-semibold text-zinc-200 block">Habilitar IA</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={!!aiLimitsState.enabled}
                          onChange={() => handleAILimitToggle('enabled')}
                          className="w-4 h-4 rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0"
                        />
                      </div>

                      <div className="flex items-center justify-between bg-zinc-900/60 border border-zinc-850 p-3 rounded-lg">
                        <div>
                          <span className="text-xs font-semibold text-zinc-200 block">Cota Compartilhada</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={!!aiLimitsState.shared_across_seats}
                          onChange={() => handleAILimitToggle('shared_across_seats')}
                          className="w-4 h-4 rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0"
                        />
                      </div>

                      <div className="flex items-center justify-between bg-zinc-900/60 border border-zinc-850 p-3 rounded-lg">
                        <div>
                          <span className="text-xs font-semibold text-zinc-200 block">Bloquear Excesso</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={!!aiLimitsState.block_on_quota_exceeded}
                          onChange={() => handleAILimitToggle('block_on_quota_exceeded')}
                          className="w-4 h-4 rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Modules control */}
                  {aiLimitsState.enabled && (
                    <div className="border-t border-zinc-800/60 pt-4 flex flex-col md:flex-row gap-4">
                      
                      {/* Left selector */}
                      <div className="w-full md:w-1/3 flex flex-col gap-2">
                        <span className="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1 block">Selecione o Recurso</span>
                        {[
                          { key: 'suggest', label: 'Expandir Ideia (Suggest)' },
                          { key: 'summary', label: 'Resumidor (Summary)' },
                          { key: 'autolink', label: 'Conexões Auto-link' },
                          { key: 'rag_chat', label: 'Chat Assistente RAG' },
                          { key: 'voice_agent', label: 'Agente de Voz' },
                          { key: 'embedding', label: 'Modelo de Embedding' }
                        ].map((mod) => (
                          <button
                            key={mod.key}
                            type="button"
                            onClick={() => setSelectedModule(mod.key as any)}
                            className={`w-full text-left px-3 py-2 text-xs font-semibold rounded border transition-all ${
                              selectedModule === mod.key
                                ? 'bg-cyan-950/40 text-cyan-400 border-cyan-900/50 shadow-md'
                                : 'bg-zinc-950/30 border-zinc-850/80 text-zinc-400 hover:text-zinc-200'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span>{mod.label}</span>
                              {aiLimitsState.modules?.[mod.key]?.enabled ? (
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              ) : (
                                <span className="w-1.5 h-1.5 rounded-full bg-zinc-650"></span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Right Settings Pane */}
                      <div className="flex-1 bg-zinc-950/30 border border-zinc-800 p-4 rounded-lg space-y-4">
                        {aiLimitsState.modules?.[selectedModule] ? (
                          <div>
                            <div className="flex items-center justify-between mb-4 pb-2 border-b border-zinc-800/40">
                              <h4 className="text-xs font-bold text-zinc-200 uppercase tracking-wider">
                                Limites do Módulo: {selectedModule}
                              </h4>
                              <label className="flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!aiLimitsState.modules[selectedModule].enabled}
                                  onChange={(e) => handleModuleLimitChange(selectedModule, 'enabled', e.target.checked)}
                                  className="rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0"
                                />
                                Ativado
                              </label>
                            </div>

                            {aiLimitsState.modules[selectedModule].enabled ? (
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-[9px] uppercase text-zinc-500 block mb-1 font-bold">Provedor IA</label>
                                    <select
                                      value={aiLimitsState.modules[selectedModule].provider || ''}
                                      onChange={(e) => handleModuleLimitChange(selectedModule, 'provider', e.target.value)}
                                      className="w-full bg-zinc-950 border border-zinc-850 rounded px-2.5 py-1.5 text-xs text-zinc-300 focus:outline-none"
                                    >
                                      <option value="openai">OpenAI</option>
                                      <option value="google">Google Gemini</option>
                                      <option value="openrouter">OpenRouter</option>
                                    </select>
                                  </div>

                                  <div>
                                    <label className="text-[9px] uppercase text-zinc-500 block mb-1 font-bold">Nome do Modelo</label>
                                    <input
                                      value={aiLimitsState.modules[selectedModule].model || ''}
                                      onChange={(e) => handleModuleLimitChange(selectedModule, 'model', e.target.value)}
                                      className="w-full bg-zinc-950 border border-zinc-850 rounded px-2.5 py-1 text-xs text-zinc-200 focus:outline-none"
                                    />
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-[9px] uppercase text-zinc-500 block mb-1 font-bold">Tokens Mensais</label>
                                    <input
                                      type="number"
                                      value={aiLimitsState.modules[selectedModule].monthly_tokens || 0}
                                      onChange={(e) => handleModuleLimitChange(selectedModule, 'monthly_tokens', parseInt(e.target.value) || 0)}
                                      className="w-full bg-zinc-950 border border-zinc-850 rounded px-2.5 py-1 text-xs text-zinc-200 focus:outline-none font-mono"
                                    />
                                  </div>

                                  <div>
                                    <label className="text-[9px] uppercase text-zinc-500 block mb-1 font-bold">Requisições Mensais</label>
                                    <input
                                      type="number"
                                      value={aiLimitsState.modules[selectedModule].monthly_requests || 0}
                                      onChange={(e) => handleModuleLimitChange(selectedModule, 'monthly_requests', parseInt(e.target.value) || 0)}
                                      className="w-full bg-zinc-950 border border-zinc-850 rounded px-2.5 py-1 text-xs text-zinc-200 focus:outline-none font-mono"
                                    />
                                  </div>
                                </div>

                                {/* Custom Parameters for RAG */}
                                {(selectedModule === 'rag_chat') && (
                                  <div className="bg-zinc-900/60 border border-zinc-850 rounded-lg p-3 space-y-3 mt-3">
                                    <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider block">Parâmetros RAG</span>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="text-[8px] uppercase text-zinc-500 block">Top K Resultados</label>
                                        <input
                                          type="number"
                                          value={aiLimitsState.modules[selectedModule].top_k_results || 5}
                                          onChange={(e) => handleModuleLimitChange(selectedModule, 'top_k_results', parseInt(e.target.value) || 5)}
                                          className="w-full bg-zinc-950 border border-zinc-850 rounded px-2 py-0.5 text-xs text-zinc-200 focus:outline-none font-mono"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-[8px] uppercase text-zinc-500 block">Limiar de Similaridade</label>
                                        <input
                                          type="number"
                                          step="0.05"
                                          min="0"
                                          max="1"
                                          value={aiLimitsState.modules[selectedModule].similarity_threshold || 0.7}
                                          onChange={(e) => handleModuleLimitChange(selectedModule, 'similarity_threshold', parseFloat(e.target.value) || 0.7)}
                                          className="w-full bg-zinc-950 border border-zinc-850 rounded px-2 py-0.5 text-xs text-zinc-200 focus:outline-none font-mono"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-6 text-zinc-500 text-xs">Este módulo está inativo neste plano.</div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-6">
                            <p className="text-zinc-500 text-xs mb-3">Módulo não configurado neste plano.</p>
                            <button
                              type="button"
                              onClick={() =>
                                setAiLimitsState((prev: any) => {
                                  const base = prev.modules?.rag_chat ?? {
                                    provider: 'openrouter',
                                    model: '',
                                    monthly_tokens: 1000000,
                                    monthly_requests: 1000,
                                  };
                                  return {
                                    ...prev,
                                    modules: {
                                      ...(prev.modules ?? {}),
                                      [selectedModule]: { ...base, enabled: true },
                                    },
                                  };
                                })
                              }
                              className="px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-zinc-950 font-bold text-xs rounded"
                            >
                              Configurar módulo
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer buttons */}
            <div className="p-4 border-t border-zinc-800 bg-zinc-950/20 flex justify-end gap-3 z-10">
              <button
                type="button"
                onClick={async () => {
                  if (!selected) return;
                  if (
                    !window.confirm(
                      'Re-apontar TODAS as assinaturas deste plano para a última versão? Aplica preço/IA novos aos assinantes atuais.',
                    )
                  )
                    return;
                  try {
                    const r = await applyLatest.mutateAsync(selected.slug);
                    window.alert(`Aplicado: ${r.updated} assinatura(s) → versão ${r.version}.`);
                  } catch {
                    window.alert('Falha ao aplicar a assinantes.');
                  }
                }}
                disabled={applyLatest.isPending}
                className="px-4 py-2 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors disabled:opacity-50"
              >
                {applyLatest.isPending ? 'Aplicando…' : 'Aplicar a assinantes'}
              </button>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="px-4 py-2 text-xs font-semibold text-zinc-400 hover:text-zinc-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={updatePlan.isPending}
                className="px-5 py-2 bg-cyan-500 hover:bg-cyan-400 text-zinc-950 font-bold text-xs rounded transition-colors shadow-lg shadow-cyan-950/25 disabled:opacity-50"
              >
                {updatePlan.isPending ? 'Salvando…' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
